import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, setDoc, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import { hasActiveCard, confirmCardRequired } from '../utils/cardGate';
import { invalidateReceivables } from '../utils/receivables';
import { invalidateSummaryCache } from './statement/SummaryCards';
import { ArrowLeft, Plus, Trash2, Save, Send, FileText, Users } from 'lucide-react';

// 로컬(KST) 기준 날짜 문자열 — toISOString은 UTC라 00~09시에 하루 밀림
const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => fmtLocalDate(new Date());

// 계산서번호: 날짜+시분초 — 전체 컬렉션 조회는 보안규칙에 막혀 항상 -001이 되던 버그 수정
const generateInvoiceNumber = () => {
    const now = new Date();
    const dateStr = fmtLocalDate(now).replace(/-/g, '');
    const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    return `TI-${dateStr}-${timeStr}`;
};

// 명세서의 실제 할인액(원) — percent형은 totalDiscount가 %값이므로 discountAmount(원)를 우선 사용
const statementDiscountAmount = (stmt) => {
    const saved = Number(stmt.discountAmount);
    if (!isNaN(saved) && saved > 0) return saved;
    const raw = Number(stmt.totalDiscount) || 0;
    if (raw <= 0) return 0;
    if (stmt.discountType === 'percent') {
        return Math.round((Number(stmt.subtotal) || 0) * raw / 100);
    }
    return raw;
};

const emptyItem = () => ({ receiptDate: '', deliveryDate: '', description: '', quantity: 1, unitPrice: 0, amount: 0 });

const parseDate = (val) => {
    if (!val) return '';
    try {
        if (val.toDate) return fmtLocalDate(val.toDate());
        if (val.seconds) return fmtLocalDate(new Date(val.seconds * 1000));
        if (typeof val === 'string' && val.length >= 10) return fmtLocalDate(new Date(val));
    } catch {}
    return '';
};

function CreateTaxInvoice({ user, onBack, fromStatement }) {
    const targetId = user?.companyId || user?.uid;

    // 거래처 목록
    const [partners, setPartners] = useState([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState('');

    // 정산된 거래명세서 목록
    const [statements, setStatements] = useState([]);
    const [selectedStatement, setSelectedStatement] = useState(null);
    const [loadingStatements, setLoadingStatements] = useState(true);

    const [saving, setSaving] = useState(false);
    // 발행 카드 게이트 — 미등록이면 상단 안내 배너 (발행 시점에도 이중 차단)
    const [cardOk, setCardOk] = useState(null);
    useEffect(() => {
        let alive = true;
        hasActiveCard(user).then(ok => { if (alive) setCardOk(ok); });
        return () => { alive = false; };
    }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

    // 폼 필드
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [issueDate, setIssueDate] = useState(today());
    const [remarks, setRemarks] = useState('');

    // 공급자 정보 — Firestore에서 직접 로드
    const [supplierInfo, setSupplierInfo] = useState({
        name: '', bizNumber: '', ceoName: '', address: '',
        bizType: '', bizItem: '', userId: targetId,
    });

    const [buyerInfo, setBuyerInfo] = useState({
        name: '', bizNumber: '', ceoName: '', address: '',
        bizType: '', bizItem: '', userId: '',
    });

    const [items, setItems] = useState([emptyItem()]);
    // 면세 기본값 (대표 확정 2026-08-25, 면세 검수 회의 #41-①): 치과기공 용역은 부가세 면세(부가세법 §26①5호,
    // 시행령 §35 치과기공사 용역)라 기본 = 면세 '계산서'. 직전 발행 때의 선택을 기억해 그대로 쓴다.
    // CAD 외주(디자인 용역)는 과세/면세 유권해석 미확정 — 기본값 강제 없이 사용자가 선택(화면 안내 참조).
    const [isTaxExempt, setIsTaxExempt] = useState(() => {
        try {
            const saved = localStorage.getItem('dcTaxExemptLast');
            if (saved === 'true') return true;
            if (saved === 'false') return false;
        } catch (_) { /* 저장소 접근 불가 시 기본값 */ }
        return true;   // 기본 = 면세(계산서)
    });
    // 골드(금값) 파생 행 안전장치 (2026-08-20 대표 확정: 금값=면세) —
    // 명세서의 itemType 'gold' 행은 과세 공급가액 계산에서 제외하고 안내만 표시한다.
    // 정밀 세무 처리(면세분 계산서 자동 생성·과세/면세 병행 발행 등)는 세무사 확인 후 확장 예정 — 그 전까지 세액 로직 무변경.
    const [goldExemptAmount, setGoldExemptAmount] = useState(0);
    const goldAmountOf = (stmt) => (stmt.items || [])
        .filter(it => it.itemType === 'gold')
        .reduce((s, it) => s + (Number(it.totalPrice ?? it.price ?? 0) || 0), 0);
    const [purposeType, setPurposeType] = useState('청구'); // 영수 / 청구
    const [invoiceeType, setInvoiceeType] = useState('사업자'); // 사업자 / 개인 / 외국인

    // 합계 계산
    const supplyAmount = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const taxAmount = isTaxExempt ? 0 : Math.round(supplyAmount * 0.1);
    const totalAmount = supplyAmount + taxAmount;

    // 공급자 정보를 Firestore에서 직접 로드 (최신 프로필 반영)
    const loadSupplierInfo = async () => {
        try {
            const uid = targetId;
            if (!uid) return;
            const userSnap = await getDoc(doc(db, 'users', uid));
            if (userSnap.exists()) {
                const ud = userSnap.data();
                setSupplierInfo({
                    name: ud.businessName || ud.name || '',
                    bizNumber: ud.businessNumber || '',
                    ceoName: ud.representativeName || '',
                    address: ud.address || '',
                    bizType: ud.bizType || '',
                    bizItem: ud.bizCategory || '',
                    userId: uid,
                });
            }
        } catch (e) {
            console.error('공급자 정보 로드 실패:', e);
        }
    };

    // 거래처 목록 로드 (connections 기반)
    const fetchPartners = async () => {
        try {
            // 직원 계정도 회사(companyId) 기준으로 연결을 조회해야 거래처가 보임
            const myId = targetId || auth.currentUser?.uid;
            if (!myId) return;

            const partnersMap = new Map();
            // 내 연결만 조회 (전체 스캔 방지)
            const [sentSnap, recvSnap] = await Promise.all([
                getDocs(query(collection(db, 'connections'), where('requesterId', '==', myId), where('status', '==', 'accepted'))),
                getDocs(query(collection(db, 'connections'), where('receiverId', '==', myId), where('status', '==', 'accepted'))),
            ]);

            const partnerIds = new Set();
            [...sentSnap.docs, ...recvSnap.docs].forEach(docSnap => {
                const conn = docSnap.data();
                const partnerId = conn.requesterId === myId ? conn.receiverId : conn.requesterId;
                if (partnerId && partnerId !== myId) partnerIds.add(partnerId);
            });

            // 상대 정보 병렬 조회 (순차 getDoc N+1 제거)
            await Promise.all(Array.from(partnerIds).map(async (partnerId) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', partnerId));
                    if (userDoc.exists()) {
                        const ud = userDoc.data();
                        partnersMap.set(partnerId, {
                            id: partnerId,
                            name: ud.businessName || ud.name || '이름 없음',
                            businessNumber: ud.businessNumber || '',
                            representativeName: ud.representativeName || '',
                            address: ud.address || '',
                            bizType: ud.bizType || '',
                            bizCategory: ud.bizCategory || '',
                        });
                    }
                } catch (err) {
                    console.error('거래처 정보 조회 실패:', err);
                }
            }));

            setPartners(Array.from(partnersMap.values()));
        } catch (e) {
            console.error('거래처 목록 조회 실패:', e);
        }
    };

    // 거래처 선택 시 공급받는자 자동 채우기
    const handlePartnerSelect = (partnerId) => {
        setSelectedPartnerId(partnerId);
        if (!partnerId) {
            setBuyerInfo({ name: '', bizNumber: '', ceoName: '', address: '', bizType: '', bizItem: '', userId: '' });
            return;
        }
        const partner = partners.find(p => p.id === partnerId);
        if (partner) {
            setBuyerInfo({
                name: partner.name,
                bizNumber: partner.businessNumber,
                ceoName: partner.representativeName,
                address: partner.address,
                bizType: partner.bizType,
                bizItem: partner.bizCategory,
                userId: partner.id,
            });
        }
    };

    const fetchStatements = async () => {
        setLoadingStatements(true);
        try {
            const currentUid = auth.currentUser?.uid;
            const col = collection(db, 'transactionStatements');

            // targetId와 uid 둘 다 조회 (웹은 uid, 앱은 targetId로 생성할 수 있음)
            const ids = new Set([targetId, currentUid].filter(Boolean));
            const allDocs = [];

            for (const id of ids) {
                try {
                    // 복합 인덱스 없이 fromUserId만 쿼리 → JS에서 필터
                    const q = query(col, where('fromUserId', '==', id));
                    const snap = await getDocs(q);
                    snap.docs.forEach(d => {
                        const data = d.data();
                        // 회원 치과: 정산완료 건 / 비회원 치과(수기·agent_·이메일): 정산 주체가 없으므로 발행됨 이상이면 포함
                        const nonMember = !data.toUserId || String(data.toUserId).startsWith('agent_') || String(data.toUserId).startsWith('email_');
                        // CAD 외주(랩↔랩): 대금은 직접 정산이고 계산서 발행은 공급자 의무 — 상대 정산확인(settled)을
                        // 기다리게 묶지 않고 발행됨(confirmed)이면 바로 후보에 포함 (대표 확인 2026-08-22)
                        const eligible = data.settlementStatus === 'settled' ||
                            (nonMember && ['issued', 'confirmed', 'settled'].includes(data.status)) ||
                            (data.kind === 'design_outsource' && data.status === 'confirmed');
                        // 발행처가 삭제·정정한 명세서는 후보에서 제외 (2026-10-23 검토 회의 — 정정본과 이중 발행 방지)
                        if (eligible && !data.deletedBySender && !allDocs.some(x => x.id === d.id)) {
                            allDocs.push({ id: d.id, ...data });
                        }
                    });
                } catch (e) {
                    console.error(`거래명세서 조회 실패 (${id}):`, e);
                }
            }

            const list = allDocs.filter(s => !s.taxInvoiceId);
            setStatements(list);
        } catch (e) {
            console.error('거래명세서 로드 실패:', e);
        }
        setLoadingStatements(false);
    };

    const handleSelectStatement = async (stmt) => {
        setSelectedStatement(stmt);

        // 재선택 시 이전 명세서 잔재 방지 — 품목·공급받는자 초기화 후 채움
        setItems([{ receiptDate: '', deliveryDate: '', description: '', quantity: 1, unitPrice: 0, amount: 0 }]);
        setGoldExemptAmount(0);   // 이전 명세서의 금값 안내 잔재 방지
        setBuyerInfo({ name: '', bizNumber: '', ceoName: '', address: '', bizType: '', bizItem: '', userId: '' });

        // 공급자/공급받는자 자동 채우기 (+ 이전 발행 때 저장한 거래처 정보로 빈 칸 채움)
        if (stmt.supplierInfo) {
            setSupplierInfo(prev => ({ ...prev, ...stmt.supplierInfo }));
        }
        const savedInfo = await loadSavedBuyerInfo(stmt.toUserId);
        if (stmt.buyerInfo) {
            setBuyerInfo(prev => fillEmpty({ ...prev, ...stmt.buyerInfo }, savedInfo));
        } else if (stmt.toUserId) {
            // toUserId로 거래처 정보 매칭
            const partner = partners.find(p => p.id === stmt.toUserId);
            if (partner) {
                setBuyerInfo(fillEmpty({
                    name: partner.name,
                    bizNumber: partner.businessNumber,
                    ceoName: partner.representativeName,
                    address: partner.address,
                    bizType: partner.bizType,
                    bizItem: partner.bizCategory,
                    userId: partner.id,
                }, savedInfo));
            } else {
                setBuyerInfo(prev => fillEmpty({
                    ...prev,
                    name: stmt.toUserName || '',
                    userId: stmt.toUserId || '',
                }, savedInfo));
            }
        }

        // 품목 채우기 — 금값(itemType 'gold') 행은 면세이므로 과세 공급가액에서 제외 (아래 안내로 별도 고지)
        if (stmt.items && stmt.items.length > 0) {
            // 금값 행 제외는 과세(세금계산서)일 때만 (면세 검수 P2-1, 2026-08-25) — 면세 계산서는 금값 포함 전체가 대상.
            // 선택 후 면세/과세를 바꾸면 명세서를 다시 선택해야 반영됩니다(토글 옆 안내 참조).
            setGoldExemptAmount(isTaxExempt ? 0 : goldAmountOf(stmt));
            const newItems = stmt.items.filter(it => isTaxExempt || it.itemType !== 'gold').map(it => ({
                receiptDate: parseDate(it.createdAt) || parseDate(it.date),
                deliveryDate: parseDate(it.dueDate),
                // 명세서 스키마는 patientName/toothInfo — populateFromStatement 경로와 동일하게 (품목명 공란 버그 수정)
                // 환자명은 넣지 않음 (보안 P0-6, 2026-10-23) — 품목명은 국세청·치과에 그대로 전송됨. 치식·보철명만
                description: String((it.toothInfo && it.toothInfo !== '-' ? it.toothInfo : '') || it.prosthesisName || it.serviceName || it.description || '').trim(),
                quantity: it.quantity || it.teethCount || it.toothCount || 1,
                unitPrice: it.unitPrice || it.pricePerTooth || 0,
                amount: it.totalPrice || it.price || it.amount || 0,
            }));
            // 명세서 할인 반영 — 실청구액과 공급가액 일치 (마이너스 품목)
            // percent형 할인은 discountAmount(원) 기준 — totalDiscount(%)를 원으로 쓰던 금액 오류 수정
            const stmtDiscount = statementDiscountAmount(stmt);
            if (stmtDiscount > 0) {
                newItems.push({ receiptDate: '', deliveryDate: '', description: '할인', quantity: 1, unitPrice: -stmtDiscount, amount: -stmtDiscount });
            }
            setItems(newItems.length > 0 ? newItems : [emptyItem()]);   // 전부 금값 행인 극단 케이스 — 빈 표 방지
        }
    };

    // ── 거래처 사업자정보 저장/불러오기 (partnerBizInfos) — 한 번 입력하면 다음 발행 때 자동 채움 ──
    const partnerKeyOf = (info) => info?.userId || String(info?.bizNumber || '').replace(/[^0-9]/g, '') || null;
    const loadSavedBuyerInfo = async (partnerKey) => {
        if (!partnerKey) return null;
        try {
            const snap = await getDoc(doc(db, 'partnerBizInfos', `${targetId}_${partnerKey}`));
            return snap.exists() ? snap.data().buyerInfo : null;
        } catch { return null; }
    };
    // 빈 필드만 저장된 값으로 채움 (직접 입력한 값이 항상 우선)
    const fillEmpty = (base, saved) => {
        if (!saved) return base;
        const out = { ...base };
        for (const k of ['name', 'bizNumber', 'ceoName', 'address', 'bizType', 'bizItem', 'email', 'phone']) {
            if (!out[k] && saved[k]) out[k] = saved[k];
        }
        return out;
    };
    const saveBuyerInfoForReuse = async (info) => {
        const key = partnerKeyOf(info);
        if (!key || !(info.name || info.bizNumber)) return;
        try {
            await setDoc(doc(db, 'partnerBizInfos', `${targetId}_${key}`), {
                userId: targetId,
                partnerKey: key,
                buyerInfo: info,
                updatedAt: Timestamp.now(),
            }, { merge: true });
        } catch (e) { console.log('거래처 정보 저장 실패(무시):', e); }
    };

    // ── 발행 거래처 정보장 — 저장된 치과 정보 목록을 열어 직접 불러오기/삭제 ──
    const [partnerBook, setPartnerBook] = useState([]);
    const [showPartnerBook, setShowPartnerBook] = useState(false);
    const openPartnerBook = async () => {
        try {
            const snap = await getDocs(query(collection(db, 'partnerBizInfos'), where('userId', '==', targetId)));
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
            setPartnerBook(rows);
            setShowPartnerBook(true);
        } catch (e) {
            console.error('거래처 정보장 로드 실패:', e);
            alert('저장된 거래처 정보를 불러오지 못했습니다.');
        }
    };
    const applyPartnerBookEntry = (entry) => {
        setBuyerInfo(prev => ({ ...prev, ...(entry.buyerInfo || {}) }));
        setShowPartnerBook(false);
    };
    const deletePartnerBookEntry = async (entry) => {
        if (!window.confirm(`'${entry.buyerInfo?.name || '거래처'}' 저장 정보를 삭제하시겠습니까?`)) return;
        try {
            const { deleteDoc: del } = await import('firebase/firestore');
            await del(doc(db, 'partnerBizInfos', entry.id));
            setPartnerBook(prev => prev.filter(x => x.id !== entry.id));
        } catch (e) { alert('삭제에 실패했습니다.'); }
    };

    // 거래명세서에서 바로 연동 (ViewTransactionStatement → navigate state)
    const populateFromStatement = async (stmt) => {
        setSelectedStatement(stmt);

        // 공급자 정보: supplierInfo가 있으면 사용, 없으면 Firestore에서 로드
        if (stmt.supplierInfo) {
            setSupplierInfo(prev => ({ ...prev, ...stmt.supplierInfo }));
        }

        // 공급받는자: buyerInfo가 있으면 사용, 없으면 toUserId로 Firestore에서 로드
        // 마지막에 이전 발행 때 저장해둔 거래처 정보(partnerBizInfos)로 빈 칸을 채움
        const saved = await loadSavedBuyerInfo(stmt.toUserId);
        if (stmt.buyerInfo && Object.keys(stmt.buyerInfo).length > 0) {
            setBuyerInfo(prev => fillEmpty({ ...prev, ...stmt.buyerInfo }, saved));
        } else if (stmt.toUserId) {
            try {
                const userSnap = await getDoc(doc(db, 'users', stmt.toUserId));
                if (userSnap.exists()) {
                    const ud = userSnap.data();
                    setBuyerInfo(fillEmpty({
                        name: ud.businessName || ud.name || stmt.toUserName || '',
                        bizNumber: ud.businessNumber || '',
                        ceoName: ud.representativeName || '',
                        address: ud.address || '',
                        bizType: ud.bizType || '',
                        bizItem: ud.bizCategory || '',
                        userId: stmt.toUserId,
                    }, saved));
                } else {
                    // 비회원(수기·agent_·이메일 거래처) — 저장된 정보가 있으면 그대로 복원
                    setBuyerInfo(prev => fillEmpty({ ...prev, name: stmt.toUserName || '', userId: stmt.toUserId || '' }, saved));
                }
            } catch (e) {
                setBuyerInfo(prev => fillEmpty({ ...prev, name: stmt.toUserName || '', userId: stmt.toUserId || '' }, saved));
            }
        }

        // 품목 채우기 — 금값(itemType 'gold') 행은 면세이므로 과세 공급가액에서 제외 (아래 안내로 별도 고지)
        if (stmt.items && stmt.items.length > 0) {
            // 금값 행 제외는 과세(세금계산서)일 때만 (면세 검수 P2-1, 2026-08-25) — 면세 계산서는 금값 포함 전체가 대상.
            // 선택 후 면세/과세를 바꾸면 명세서를 다시 선택해야 반영됩니다(토글 옆 안내 참조).
            setGoldExemptAmount(isTaxExempt ? 0 : goldAmountOf(stmt));
            const newItems = stmt.items.filter(it => isTaxExempt || it.itemType !== 'gold').map(it => ({
                receiptDate: parseDate(it.createdAt) || parseDate(it.date),
                deliveryDate: parseDate(it.dueDate),
                // 환자명은 넣지 않음 (보안 P0-6, 2026-10-23) — 품목명은 국세청·치과에 그대로 전송됨. 치식·보철명만
                description: String((it.toothInfo && it.toothInfo !== '-' ? it.toothInfo : '') || it.prosthesisName || it.serviceName || it.description || '').trim(),
                quantity: it.quantity || it.teethCount || it.toothCount || 1,
                unitPrice: it.unitPrice || it.pricePerTooth || 0,
                amount: it.totalPrice || it.price || it.amount || 0,
            }));
            // 명세서 할인 반영 — 실청구액과 공급가액 일치 (마이너스 품목)
            // percent형 할인은 discountAmount(원) 기준 — totalDiscount(%)를 원으로 쓰던 금액 오류 수정
            const stmtDiscount = statementDiscountAmount(stmt);
            if (stmtDiscount > 0) {
                newItems.push({ receiptDate: '', deliveryDate: '', description: '할인', quantity: 1, unitPrice: -stmtDiscount, amount: -stmtDiscount });
            }
            setItems(newItems.length > 0 ? newItems : [emptyItem()]);   // 전부 금값 행인 극단 케이스 — 빈 표 방지
        }
        setLoadingStatements(false);
    };

    useEffect(() => {
        setInvoiceNumber(generateInvoiceNumber());
        loadSupplierInfo();
        fetchPartners().then(() => {
            if (fromStatement) {
                populateFromStatement(fromStatement);
            } else {
                fetchStatements();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClearStatement = () => {
        setSelectedStatement(null);
        setSelectedPartnerId('');
        setItems([emptyItem()]);
        setBuyerInfo({ name: '', bizNumber: '', ceoName: '', address: '', bizType: '', bizItem: '', userId: '' });
    };

    const updateItem = (idx, field, value) => {
        setItems(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            if (field === 'quantity' || field === 'unitPrice') {
                const qty = field === 'quantity' ? Number(value) : Number(next[idx].quantity);
                const up = field === 'unitPrice' ? Number(value) : Number(next[idx].unitPrice);
                next[idx].amount = qty * up;
            }
            return next;
        });
    };

    const addItem = () => setItems(prev => [...prev, emptyItem()]);
    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    // 덴클 미가입 거래처인가 — 계정이 없거나(수기 입력), 파일에서 이름만 읽어온 치과(agent_)·이메일 거래처(email_)
    const isNonMemberBuyer = !buyerInfo.userId
        || String(buyerInfo.userId).startsWith('agent_')
        || String(buyerInfo.userId).startsWith('email_');

    const handleSave = async (status) => {
        // 더블클릭·동시 제출 가드 (면세 검수 P1-3, 2026-08-25) — 같은 명세서로 계산서 2장 생성 차단
        if (saving) return;
        // 검수 P1-B (2026-08-20): 금값(면세) 제외 후 할인이 과세분을 초과하면 공급가액이 음수가 됨 — 발행 차단
        if (supplyAmount < 0) {
            alert('공급가액이 음수입니다 — 할인 금액이 과세 품목 합계보다 큽니다.\n(면세 금값은 세금계산서 대상이 아니라 제외됩니다)\n할인 금액을 조정하거나, 금값 배분은 세무사와 상의해 주세요.');
            return;
        }
        if (!buyerInfo.name) {
            alert('공급받는자(거래처)를 선택하세요.');
            return;
        }
        // 발행은 유료 기능 — 결제 카드 등록 필수 (임시저장 draft는 허용)
        if (status === 'issued' && !(await hasActiveCard(user))) {
            confirmCardRequired();
            return;
        }
        // 비회원 치과도 발행 허용 (2026-07-23) — 사업자번호만 있으면 전자발행 가능. 회원이 아니면 알림만 생략
        if (status === 'issued' && !buyerInfo.userId && !buyerInfo.bizNumber) {
            alert('발행하려면 공급받는자의 사업자등록번호를 입력해주세요.\n(덴클 미가입 치과도 사업자번호로 발행할 수 있습니다)');
            return;
        }
        // 미가입 치과 발행 전 확인 (2026-07-30 대표 결정) — 상대가 덴클 회원이 아니면
        // 치과 화면에 뜨지도, 알림이 가지도 않는다. 이름이 파일에서 자동으로 읽힌 것일 수 있어
        // 발행 전에 사람이 한 번 더 상호·사업자번호를 확인하게 한다.
        // 회원 거래처 발행 확인창 (면세 검수 UX P0-2, 2026-08-25) — 임시저장 옆 [발행] 오클릭 방지 + 서류 종류(면세/과세) 확인
        if (status === 'issued' && !isNonMemberBuyer) {
            const docType1 = isTaxExempt ? '계산서 (면세 — 세액 0원)' : '세금계산서 (과세 — 세액 10% 포함)';
            const ok1 = window.confirm(
                `${buyerInfo.name || '거래처'}에게 ${docType1}를 발행합니다.\n` +
                `공급가액 ${supplyAmount.toLocaleString()}원 · 세액 ${taxAmount.toLocaleString()}원 · 합계 ${totalAmount.toLocaleString()}원\n\n` +
                `발행할까요?`
            );
            if (!ok1) { return; }
        }
        if (status === 'issued' && isNonMemberBuyer) {
            const docType0 = isTaxExempt ? '계산서' : '세금계산서';
            const ok = window.confirm(
                `덴클에 가입하지 않은 거래처입니다.\n\n` +
                `상호: ${buyerInfo.name || '(없음)'}\n` +
                `사업자등록번호: ${buyerInfo.bizNumber || '(없음)'}\n\n` +
                `상호·사업자등록번호가 맞는지 확인해주세요. 잘못 발행하면 수정발행을 해야 합니다.\n` +
                `이 ${docType0}는 치과 화면에 뜨지 않고 알림도 가지 않습니다 — 인쇄·PDF로 전달하셔야 합니다.\n\n` +
                `발행할까요?`
            );
            if (!ok) { return; }
        }
        setSaving(true);
        try {
            const data = {
                invoiceNumber,
                issueDate,
                remarks,
                supplierInfo,
                buyerInfo,
                items: items.map(it => ({
                    ...it,
                    quantity: Number(it.quantity) || 0,
                    unitPrice: Number(it.unitPrice) || 0,
                    amount: Number(it.amount) || 0,
                })),
                supplyAmount,
                taxAmount,
                totalAmount,
                isTaxExempt,
                purposeType,
                invoiceeType,
                status,
                createdBy: targetId,
                createdAt: Timestamp.now(),
                linkedStatementId: selectedStatement?.id || null,
            };

            const docRef = await addDoc(collection(db, 'taxInvoices'), data);
            // 원장(받을 돈)·요약 카드 캐시 지우기 (명세 R-3) — 발행 세액·명세서 연결이 바뀜
            invalidateReceivables(targetId);
            invalidateSummaryCache(targetId);

            // 입력한 거래처 사업자정보 저장 — 다음 발행 때 자동으로 채워짐 (실패해도 발행에는 영향 없음)
            saveBuyerInfoForReuse(buyerInfo);
            try { localStorage.setItem('dcTaxExemptLast', String(isTaxExempt)); } catch (_) { /* 저장소 불가 시 무시 */ }

            // 연결된 거래명세서 업데이트
            if (selectedStatement?.id) {
                await updateDoc(doc(db, 'transactionStatements', selectedStatement.id), {
                    taxInvoiceId: docRef.id,
                    taxInvoiceStatus: status,
                });
            }

            // 발행 시 공급받는자(치과)에게 수신 알림 — 확인 작업 유도
            if (status === 'issued' && buyerInfo.userId && !String(buyerInfo.userId).startsWith('agent_') && !String(buyerInfo.userId).startsWith('email_')) {
                try {
                    await createNotification({
                        recipientId: buyerInfo.userId,
                        type: 'tax_invoice_received',
                        title: isTaxExempt ? '계산서 수신' : '세금계산서 수신',
                        message: `${supplierInfo.name || '거래처'}에서 ${isTaxExempt ? '계산서' : '세금계산서'}를 발행했습니다. (${invoiceNumber} · ${totalAmount.toLocaleString()}원)\n내용을 확인해주세요.`,
                        link: '/tax-invoices',
                    });
                } catch (notifErr) {
                    console.error('세금계산서 알림 전송 실패:', notifErr);
                }
            }

            const docType = isTaxExempt ? '계산서' : '세금계산서';
            alert(status === 'issued' ? `${docType}가 발행되었습니다.` : '임시저장되었습니다.');
            onBack && onBack();
        } catch (e) {
            console.error('세금계산서 저장 실패:', e);
            if (e.code === 'permission-denied') {
                alert('저장 권한이 없습니다. 고객센터(010-2972-3733)로 문의해주세요. (내부: Firestore 보안 규칙에서 taxInvoices 컬렉션 쓰기 권한이 없습니다.\nFirebase 콘솔 → Firestore → 규칙에서 taxInvoices 컬렉션에 대한 write 규칙을 추가해주세요.');
            } else {
                alert(`저장 실패: ${e.message || e}`);
            }
        }
        setSaving(false);
    };

    const inputStyle = {
        padding: '8px 12px', borderRadius: '8px', border: '1px solid #E2E8F0',
        fontSize: '13px', width: '100%', boxSizing: 'border-box',
    };
    const readOnlyStyle = {
        ...inputStyle, background: '#F8FAFC', color: '#475569',
    };
    const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block' };
    const sectionStyle = {
        background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0',
        padding: '20px', marginBottom: '16px',
    };

    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            {/* 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <button
                    onClick={onBack}
                    style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '8px', cursor: 'pointer' }}
                >
                    <ArrowLeft size={18} />
                </button>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1E293B' }}>
                    {isTaxExempt ? '전자계산서 작성' : '전자세금계산서 작성'}
                </h2>
            </div>

            {/* 발행 유료 안내 — 카드 미등록 시 */}
            {cardOk === false && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#92400e', lineHeight: 1.7 }}>
                    <b>ⓘ 세금계산서 발행은 결제 카드 등록 후 이용할 수 있습니다</b> (전자발행 비용 발생 기능).
                    거래명세서 작성·발행·프린트는 체험 기간 동안 무료입니다. 임시저장은 카드 없이도 가능합니다.
                    <button onClick={() => { window.location.href = '/subscription'; }} style={{ marginLeft: 8, padding: '4px 10px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>카드 등록하기</button>
                </div>
            )}

            {/* 명세서 불러오기 — 명세서에서 바로 진입(fromStatement)한 경우는 이미 채워져 있으므로 숨김 */}
            {/* 2026-08-22 대표 지시 "좀 더 잘 보이게": 강조 컨테이너 + 제목 확대 + 건수 배지 */}
            {!fromStatement && (
                <div style={{ ...sectionStyle, background: '#F5F7FF', border: '1.5px solid #C7D2FE' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <FileText size={18} color="#6366F1" />
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#312E81' }}>거래명세서에서 불러오기</h3>
                        {!loadingStatements && statements.length > 0 && (
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff', background: '#6366F1', borderRadius: 10, padding: '1px 9px' }}>
                                {statements.length}건
                            </span>
                        )}
                        {selectedStatement && (
                            <button
                                onClick={handleClearStatement}
                                style={{
                                    marginLeft: 'auto', padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                    border: '1px solid #E2E8F0', borderRadius: '6px', background: '#fff',
                                    color: '#64748B', cursor: 'pointer',
                                }}
                            >
                                선택 해제
                            </button>
                        )}
                    </div>
                    <div style={{ fontSize: '12.5px', color: '#4F46E5', marginBottom: '12px', paddingLeft: '26px' }}>
                        아래에서 명세서를 선택하면 거래처·품목·금액이 자동으로 채워집니다
                    </div>
                    {loadingStatements ? (
                        <div style={{ padding: '16px 0', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                            명세서 목록을 불러오는 중...
                        </div>
                    ) : statements.length === 0 ? (
                        <div style={{ padding: '14px', background: '#F8FAFC', borderRadius: '8px', color: '#94A3B8', fontSize: '13px', textAlign: 'center' }}>
                            발행 가능한 명세서가 없습니다. (정산완료 건 · CAD 외주는 발행된 건, 세금계산서 미발행만 표시됩니다)
                            <br />거래처를 직접 선택해 작성할 수도 있습니다.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                            {statements.map(stmt => {
                                const isSelected = selectedStatement?.id === stmt.id;
                                return (
                                    <div
                                        key={stmt.id}
                                        onClick={() => handleSelectStatement(stmt)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                                            padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                                            border: isSelected ? '1.5px solid #6366F1' : '1px solid #E2E8F0',
                                            background: isSelected ? '#EEF2FF' : '#fff',
                                        }}
                                    >
                                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>
                                            {stmt.toUserName || stmt.buyerInfo?.name || '거래처 미상'}
                                        </span>
                                        {stmt.kind === 'design_outsource' && (
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#3730A3', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 6, padding: '1px 6px' }}>CAD 외주</span>
                                        )}
                                        <span style={{ fontSize: '12px', color: '#64748B' }}>
                                            {parseDate(stmt.startDate) || '-'} ~ {parseDate(stmt.endDate) || '-'}
                                        </span>
                                        <span style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: '700', color: '#4338CA', whiteSpace: 'nowrap' }}>
                                            {(Number(stmt.totalAmount) || 0).toLocaleString()}원
                                        </span>
                                        {isSelected && (
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#6366F1' }}>선택됨</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* 기본 정보 */}
            <div style={sectionStyle}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>기본 정보</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                        <label style={labelStyle}>{isTaxExempt ? '계산서 번호' : '세금계산서 번호'}</label>
                        <input
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>발행일</label>
                        <input
                            type="date"
                            value={issueDate}
                            onChange={(e) => setIssueDate(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>영수/청구</label>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            {['청구', '영수'].map(t => (
                                <button
                                    key={t}
                                    onClick={() => setPurposeType(t)}
                                    style={{
                                        flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                                        border: purposeType === t ? 'none' : '1px solid #E2E8F0', cursor: 'pointer',
                                        background: purposeType === t ? '#6366F1' : '#fff',
                                        color: purposeType === t ? '#fff' : '#475569',
                                    }}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>비고</label>
                    <input
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="비고사항 입력"
                        style={inputStyle}
                    />
                </div>
            </div>

            {/* 📒 발행 거래처 정보장 모달 */}
            {showPartnerBook && (
                <div onClick={() => setShowPartnerBook(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #F1F5F9' }}>
                            <b style={{ fontSize: 14 }}>📒 저장된 거래처 정보 ({partnerBook.length})</b>
                            <button onClick={() => setShowPartnerBook(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#94A3B8' }}>✕</button>
                        </div>
                        <div style={{ padding: '10px 18px 18px' }}>
                            {partnerBook.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: '30px 0' }}>
                                    저장된 거래처가 없습니다.<br />공급받는자 정보를 입력하고 [현재 정보 저장]을 누르거나, 발행하면 자동으로 저장됩니다.
                                </div>
                            ) : partnerBook.map(entry => (
                                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F8FAFC' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1E293B' }}>{entry.buyerInfo?.name || '(상호 없음)'}</div>
                                        <div style={{ fontSize: 11.5, color: '#94A3B8' }}>
                                            {entry.buyerInfo?.bizNumber || '사업자번호 없음'}{entry.buyerInfo?.ceoName ? ` · ${entry.buyerInfo.ceoName}` : ''}{entry.buyerInfo?.address ? ` · ${entry.buyerInfo.address}` : ''}
                                        </div>
                                    </div>
                                    <button onClick={() => applyPartnerBookEntry(entry)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366F1', color: '#fff', cursor: 'pointer', flex: 'none' }}>불러오기</button>
                                    <button onClick={() => deletePartnerBookEntry(entry)} style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, border: '1px solid #FECACA', background: '#fff', color: '#DC2626', cursor: 'pointer', flex: 'none' }}>삭제</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* 공급자 / 공급받는자 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                {/* 공급자 (자동 입력 — 내 사업자 정보) */}
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>공급자</h3>
                        <span style={{ fontSize: '11px', color: '#10B981', fontWeight: '600' }}>자동입력</span>
                    </div>
                    {[
                        ['name', '상호'],
                        ['bizNumber', '사업자번호'],
                        ['ceoName', '대표자'],
                        ['address', '주소'],
                        ['bizType', '업태'],
                        ['bizItem', '종목'],
                    ].map(([field, label]) => (
                        <div key={field} style={{ marginBottom: '10px' }}>
                            <label style={labelStyle}>{label}</label>
                            <input
                                value={supplierInfo[field] || ''}
                                onChange={(e) => setSupplierInfo(prev => ({ ...prev, [field]: e.target.value }))}
                                style={readOnlyStyle}
                            />
                        </div>
                    ))}
                </div>

                {/* 공급받는자 (거래처 선택으로 자동 입력) */}
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>공급받는자</h3>
                        <button onClick={openPartnerBook} style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4338CA', cursor: 'pointer' }}>
                            📒 저장된 거래처
                        </button>
                        <button onClick={() => { saveBuyerInfoForReuse(buyerInfo); alert(buyerInfo.name || buyerInfo.bizNumber ? '현재 거래처 정보를 저장했습니다.\n다음 발행 때 [📒 저장된 거래처]에서 불러올 수 있습니다.' : '저장할 상호 또는 사업자번호를 먼저 입력해주세요.'); }}
                            style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid #E2E8F0', background: '#fff', color: '#64748B', cursor: 'pointer' }}>
                            현재 정보 저장
                        </button>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '3px' }}>
                            {['사업자', '개인', '외국인'].map(t => (
                                <button
                                    key={t}
                                    onClick={() => setInvoiceeType(t)}
                                    style={{
                                        padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                        border: invoiceeType === t ? 'none' : '1px solid #E2E8F0', cursor: 'pointer',
                                        background: invoiceeType === t ? '#10B981' : '#fff',
                                        color: invoiceeType === t ? '#fff' : '#94A3B8',
                                    }}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 거래처 선택 드롭다운 */}
                    {!selectedStatement && (
                        <div style={{ marginBottom: '14px' }}>
                            <label style={labelStyle}>
                                <Users size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                거래처 선택
                            </label>
                            <select
                                value={selectedPartnerId}
                                onChange={(e) => handlePartnerSelect(e.target.value)}
                                style={{ ...inputStyle, cursor: 'pointer' }}
                            >
                                <option value="">거래처를 선택하세요...</option>
                                {partners.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {[
                        ['name', '상호'],
                        ['bizNumber', '사업자번호'],
                        ['ceoName', '대표자'],
                        ['address', '주소'],
                        ['bizType', '업태'],
                        ['bizItem', '종목'],
                    ].map(([field, label]) => (
                        <div key={field} style={{ marginBottom: '10px' }}>
                            <label style={labelStyle}>{label}</label>
                            <input
                                value={buyerInfo[field] || ''}
                                onChange={(e) => setBuyerInfo(prev => ({ ...prev, [field]: e.target.value }))}
                                style={buyerInfo[field] ? readOnlyStyle : inputStyle}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* 품목 */}
            <div style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>품목</h3>
                    <button
                        onClick={addItem}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            padding: '6px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0',
                            borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                        }}
                    >
                        <Plus size={14} /> 품목 추가
                    </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC' }}>
                                {['입고일', '납품일', '품목명', '수량', '단가', '금액', ''].map((h, i) => (
                                    <th key={i} style={{ padding: '8px 10px', textAlign: i <= 2 ? 'left' : 'right', fontWeight: '600', color: '#475569', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx}>
                                    <td style={{ padding: '6px 4px' }}>
                                        <input
                                            type="date"
                                            value={item.receiptDate || ''}
                                            onChange={(e) => updateItem(idx, 'receiptDate', e.target.value)}
                                            style={{ ...inputStyle, width: '130px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 4px' }}>
                                        <input
                                            type="date"
                                            value={item.deliveryDate || ''}
                                            onChange={(e) => updateItem(idx, 'deliveryDate', e.target.value)}
                                            style={{ ...inputStyle, width: '130px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 4px' }}>
                                        <input
                                            value={item.description}
                                            onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                            placeholder="품목명"
                                            style={{ ...inputStyle, minWidth: '160px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 4px' }}>
                                        <input
                                            type="number" min="0"
                                            value={item.quantity}
                                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                            style={{ ...inputStyle, width: '70px', textAlign: 'right' }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 4px' }}>
                                        <input
                                            type="number" min="0"
                                            value={item.unitPrice}
                                            onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)}
                                            style={{ ...inputStyle, width: '100px', textAlign: 'right' }}
                                        />
                                    </td>
                                    <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {(item.amount || 0).toLocaleString()}원
                                    </td>
                                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                                        {items.length > 1 && (
                                            <button
                                                onClick={() => removeItem(idx)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* 보안 P0-6 (2026-10-23): 품목명은 국세청·치과에 그대로 전송 — 환자 이름 입력 금지 안내 */}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#B45309' }}>
                    품목명은 국세청과 치과에 그대로 전송됩니다. 환자 이름을 적지 마세요.
                </div>

                {/* 합계 */}
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    {/* 금값 면세 안내 (2026-08-20 대표 확정: 금값=면세) — 명세서의 금값 행은 위 품목·공급가액에서 제외됨.
                        정밀 세무 처리(면세분 계산서 자동 생성 등)는 세무사 확인 후 확장 — 그 전까지 안내만 한다. */}
                    {goldExemptAmount > 0 && (
                        <div style={{ fontSize: '12px', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 10px', fontWeight: 600 }}>
                            면세 금값 {goldExemptAmount.toLocaleString()}원 별도(계산서 대상) — 과세 공급가액에서 제외했습니다.
                        </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#64748B' }}>
                        공급가액: <strong>{supplyAmount.toLocaleString()}원</strong>
                    </div>
                    {!isTaxExempt && (
                        <div style={{ fontSize: '13px', color: '#64748B' }}>
                            세액 (10%): <strong>{taxAmount.toLocaleString()}원</strong>
                        </div>
                    )}
                    {isTaxExempt && (
                        <div style={{ fontSize: '12px', color: '#3B82F6', fontWeight: '500' }}>
                            면세 적용 (세액 없음)
                        </div>
                    )}
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#1E293B', borderTop: '2px solid #E2E8F0', paddingTop: '8px' }}>
                        합계: {totalAmount.toLocaleString()}원
                    </div>
                </div>
            </div>

            {/* 면세 계산서 토글 */}
            <div style={{
                ...sectionStyle,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <div>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1E293B' }}>면세 계산서</span>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94A3B8' }}>공급하는 재화·용역이 부가세 면세인 경우 계산서로 발행됩니다 — 치과기공 용역은 면세입니다 (CAD 디자인 외주 등 그 외 용역은 세무사 확인 권장)</p>
                </div>
                <div
                    onClick={() => setIsTaxExempt(!isTaxExempt)}
                    style={{
                        width: '44px', height: '24px', borderRadius: '12px', flexShrink: 0,
                        background: isTaxExempt ? '#3B82F6' : '#CBD5E1',
                        position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                    }}
                >
                    <div style={{
                        width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '2px', left: isTaxExempt ? '22px' : '2px',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                </div>
            </div>

            {/* 저장 버튼 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                <button
                    onClick={() => handleSave('draft')}
                    disabled={saving}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '10px 20px', background: '#F1F5F9', color: '#475569',
                        border: '1px solid #E2E8F0', borderRadius: '10px', cursor: 'pointer',
                        fontWeight: '600', fontSize: '14px',
                    }}
                >
                    <Save size={16} /> 임시저장
                </button>
                <button
                    onClick={() => handleSave('issued')}
                    disabled={saving}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '10px 20px', background: '#6366F1', color: 'white',
                        border: 'none', borderRadius: '10px', cursor: 'pointer',
                        fontWeight: '600', fontSize: '14px',
                    }}
                >
                    <Send size={16} /> 발행
                </button>
            </div>
        </div>
    );
}

export default CreateTaxInvoice;
